import { useState } from 'react'
import { Plus, Trash2, Star } from 'lucide-react'
import { STANDARD_TEXT_SCOPES, STANDARD_TEXT_SCOPE_LABELS } from '@mavatech/shared'
import { Button, Dialog, Input, Select, Textarea } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

const SCOPES: { value: string; label: string }[] = STANDARD_TEXT_SCOPES.map((s) => ({
  value: s,
  label: STANDARD_TEXT_SCOPE_LABELS[s] ?? s,
}))
const SCOPE_LABEL = Object.fromEntries(SCOPES.map((s) => [s.value, s.label]))

export function StandardTextsTab() {
  const utils = trpc.useUtils()
  const addToast = useAppStore((s) => s.addToast)
  const list = trpc.standardText.list.useQuery()
  const [editing, setEditing] = useState<null | {
    id?: string
    scope: string
    title: string
    body: string
    isDefault: boolean
  }>(null)

  const refresh = () => utils.standardText.list.invalidate()
  const create = trpc.standardText.create.useMutation({
    onSuccess: () => {
      setEditing(null)
      refresh()
      addToast({ type: 'success', title: 'Textbaustein angelegt' })
    },
    onError: (e) => addToast({ type: 'error', title: 'Fehler', description: e.message }),
  })
  const update = trpc.standardText.update.useMutation({
    onSuccess: () => {
      setEditing(null)
      refresh()
      addToast({ type: 'success', title: 'Gespeichert' })
    },
  })
  const remove = trpc.standardText.remove.useMutation({ onSuccess: refresh })

  const grouped = SCOPES.map((s) => ({
    ...s,
    items: (list.data ?? []).filter((t) => t.scope === s.value),
  }))

  const save = () => {
    if (!editing || !editing.title.trim()) return
    if (editing.id)
      update.mutate({
        id: editing.id,
        title: editing.title.trim(),
        body: editing.body,
        isDefault: editing.isDefault,
      })
    else
      create.mutate({
        scope: editing.scope as never,
        title: editing.title.trim(),
        body: editing.body,
        isDefault: editing.isDefault,
      })
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl space-y-6">
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={() =>
              setEditing({ scope: 'quote_intro', title: '', body: '', isDefault: false })
            }
          >
            <Plus className="w-4 h-4" /> Neuer Textbaustein
          </Button>
        </div>

        {grouped.map((g) => (
          <section key={g.value}>
            <h3 className="text-sm font-semibold text-text-primary mb-2">{g.label}</h3>
            {!g.items.length ? (
              <p className="text-sm text-text-tertiary">—</p>
            ) : (
              <div className="space-y-2">
                {g.items.map((t) => (
                  <div key={t.id} className="card p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-text-primary flex items-center gap-1.5">
                        {t.isDefault && <Star className="w-3.5 h-3.5 text-accent fill-accent" />}
                        {t.title}
                      </span>
                      <div className="flex gap-2">
                        <button
                          className="text-xs text-text-secondary hover:text-text-primary"
                          onClick={() =>
                            setEditing({
                              id: t.id,
                              scope: t.scope,
                              title: t.title,
                              body: t.body,
                              isDefault: t.isDefault,
                            })
                          }
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="text-text-tertiary hover:text-danger"
                          onClick={() => remove.mutate({ id: t.id })}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap line-clamp-3">{t.body}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      <Dialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        title={editing?.id ? 'Textbaustein bearbeiten' : 'Neuer Textbaustein'}
        wide
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={save}
              loading={create.isPending || update.isPending}
              disabled={!editing?.title.trim()}
            >
              Speichern
            </Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            {!editing.id && (
              <Select
                label="Verwendung"
                value={editing.scope}
                onChange={(e) => setEditing({ ...editing, scope: e.target.value })}
                options={SCOPES}
              />
            )}
            <Input
              label="Betreff"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            />
            <Textarea
              label="Text"
              rows={6}
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={editing.isDefault}
                onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })}
              />
              als Standard für „{SCOPE_LABEL[editing.scope]}“ verwenden
            </label>
          </div>
        )}
      </Dialog>
    </div>
  )
}
